import React from 'react';
import PropTypes from 'prop-types';
import { browserHistory, Link } from 'react-router';
import { reduxForm } from 'redux-form';
import { Button } from 'react-bootstrap';
import PageHeader from '../PageHeader/PageHeader';
import RadioGroup from '../RadioGroup/RadioGroup';
import AddressSearch from '../AddressSearch/AddressSearch';
import ConnectionDateContainer from '../../redux/containers/ConnectionDateContainer';
import DashboardConstants from '../myaccount/DashboardConstants';
import PlanUtils from '../../services/PlanService';
import PlanDetails from '../Plans/PlanDetails/PlanDetails';
import MoveAddressValidator from '../../common/validation/MoveAddressValidator';
import SectionHeading from '../SectionHeading/SectionHeading';
import FormRadioGroupContainer from '../../redux/containers/FormRadioGroupContainer';
import SessionCheckerContainer from '../../redux/containers/SessionCheckerContainer';
import './Change.scss';
import CanIGoFasterMessage from './CanIGoFasterMessage';
import OhNoMessage from './OhNoMessage';
import ChangeService from '../../services/ChangeService';
import Tooltip from '../Tooltip/Tooltip';
import PlanData from '../../common/PlanDetails';
import TooltipContent from './MoveTooltipContent';

const getPlanDetails = (label) => {
  const plan = PlanUtils.getPlanData(label);
  return { planStateText: 'Current Speed', ...plan };
};

const getLosUri = () => {
  const uri = PlanData.legalOfferSummaryLink;
  return uri;
};

const getCurrentPlanSection = (isCurrentPlanAvailable, currentPlanLabel) => {
  if (isCurrentPlanAvailable) {
    return (<PlanDetails {...getPlanDetails(currentPlanLabel)} />);
  }
  return null;
};

const getSpeedSelectionSection = (params) => {
  if (params.speedChangeOption === DashboardConstants.CHANGE_OR_EXISTING_OPTIONS[1].id) {
    return (
      <div className="move-selection">
        <Tooltip {...TooltipContent.speedSelectionTooltipData}>
          <div>
            <div>
              The speeds available to you at your new address will vary depending on which technology is
              available in the street.
            </div>
            {params.ufbAvailable &&
            <div>
              We recommend our Unlimited Fibre plans if they&apos;re available, as fibre is generally
              faster and more reliable than standard broadband.
            </div>
            }
          </div>
        </Tooltip>
        <RadioGroup
          name={'plan'}
          radioButtons={
            PlanUtils.mapPlansToRadios(
              PlanUtils.getOtherPlans(params.availablePlans, params.customerInfo.currentPlan),
              params.customerInfo.currentPlan, true)
          }
          selectedValue={params.selectedPlanId}
          onChange={params.setSelectedPlan}
          underline
        />
      </div>
    );
  }
  return null;
};

const getConnectDateTooltip = params => (
  <div className="move-selection connect-date-description">
    {params.hasInstalledOnt &&
    <Tooltip {...TooltipContent.connectDateTooltipInstalledOnt} />
    }
    {params.ufbAvailable && !params.hasInstalledOnt &&
    <Tooltip {...TooltipContent.connectDateTooltipNoOnt} />
    }
    {!params.ufbAvailable &&
    <Tooltip {...TooltipContent.connectDateTooltipDSLOnly} />
    }
    <span className="plan-price-description">
      {' '}
      For a full list of plans and pricing,{' '}
      <a
        href="https://www.skinny.co.nz/broadband"
        target="_blank"
        rel="noopener noreferrer"
      >
        see here
      </a>
    </span>
  </div>
);

const Move = reduxForm({
  form: 'checkout',
  validate: MoveAddressValidator,
  destroyOnUnmount: false,
  handleNext: () => {
    const pageName = DashboardConstants.MOVE_ADDRESS;
    browserHistory.push(`/confirm-change/${pageName}`);
  }
})(props => (
  <main>
    <SessionCheckerContainer />
    <PageHeader title={DashboardConstants.MOVE_ADDRESS} />
    <section className="address-search">
      <div className="container">
        <div className="row">
          <div className="col-sm-12 autotest-address-search-get-started-header">
            <h2 className="address-search-title">{'Thanks for being with Skinny Unlimited Broadband.'}</h2>
          </div>
        </div>
      </div>
      <AddressSearch
        headingNumber={1}
        subTitle={'Check what you can get at your new place.'}
        description={'Pop your new address in here to see which Skinny Unlimited broadband plans are available.'}
        isChange
      />
    </section>
    <section className="remove-top-margin">
      {props.isAddressSelected
      && ChangeService.canMove(props) &&
      <div className="container">
        <div className="row">
          <div className="col-sm-12 col-md-9">
            <SectionHeading
              number={2}
              title={'Select your preferred connection date.'}
              titleClassName={'address-search-section-title'}
              numberClassName={'address-search-heading-number'}
              className="nullify-section-margin-top"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-9">
            <form className="nullify-section-margin-top">
              <ConnectionDateContainer />
            </form>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-9">
            {getConnectDateTooltip(props)}
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-9">
            <SectionHeading
              number={3}
              title={'Your speed at your new place.'}
              titleClassName={'address-search-section-title'}
              numberClassName={'address-search-heading-number'}
              description={null}
            />
            {getCurrentPlanSection(props.isCurrentPlanAvailable, props.customerInfo.currentPlan)}

            {props.isCurrentPlanAvailable && props.availablePlans.length > 1 &&
            <div className="container">
              <FormRadioGroupContainer
                name="speedChange"
                selectedId={props.speedChangeOption}
                radioButtons={DashboardConstants.CHANGE_OR_EXISTING_OPTIONS}
              />
            </div>
            }
            {getSpeedSelectionSection(props)}
            <div className="los-link">
              <Link
                target="_blank"
                to={getLosUri()}
                rel="noopener noreferrer"
              > Legal offer summary</Link> for your new plan.
            </div>
            <div>
                If you or someone at your place is vulnerable due to medical,
                disability, or safety reasons, visit{' '}
              <a
                href="https://www.skinny.co.nz/vulnerable"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.skinny.co.nz/vulnerable
              </a>
            </div>
          </div>
        </div>
      </div>
      }
      <OhNoMessage {...props} />
      <CanIGoFasterMessage {...props} />
      <div className="container">
        <div className="button-container">
          <Button
            disabled={
              !props.isAddressSelected
              || props.connectionDateError
              || !ChangeService.canMove(props)
            }
            type="submit"
            id="change-continue"
            onClick={props.handleNext}
          >
            Continue
          </Button>
          <Button
            id="change-back"
            className="btn btn-default btn-secondary"
            onClick={() => browserHistory.push('/dashboard')}
          >
            Back
          </Button>
        </div>
      </div>
    </section>
  </main>
));

Move.propTypes = {
  params: PropTypes.object,
  address: PropTypes.string,
  isAddressSelected: PropTypes.bool,
  customerInfo: PropTypes.shape(
    {
      currentPlan: PropTypes.string.isRequired
    }
  ),
  availablePlans: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedPlanId: PropTypes.number,
  ufbAvailable: PropTypes.bool,
  hasInstalledOnt: PropTypes.bool,
  connectionDateError: PropTypes.bool,
  meta: PropTypes.object,
  speedChangeOption: PropTypes.string,
  isCurrentPlanAvailable: PropTypes.bool,
  setSelectedPlan: PropTypes.func,
  addressSearchError: PropTypes.object
};

export default Move;



/** WEBPACK FOOTER **
 ** ./src/components/Change/Move.js
 **/